import { FC, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
	collectStats,
	combineBatStats,
	combineBowlStats,
	combineFieldStats,
	getSeasons,
	getTeamGradeIds
} from "../../cricket-api/combined-api";
import { Team } from "../../schema/schema";
import { Loader, LoadingOverlay, MultiSelect, Select } from "@mantine/core";
import { TrophyIcon, ChartBarIcon } from "@heroicons/react/24/outline";
import uniq from "lodash.uniq";
import InsightGraph from "./InsightGraph";

const Insights: FC<{ orgId: string }> = ({ orgId }) => {
	const [batStats, setBatStats] = useState<Record<string, string | number | null>[]>([]);
	const [bowlStats, setBowlStats] = useState<Record<string, string | number | null>[]>([]);
	const [batters, setBatters] = useState<string[]>([]);
	const [selectedBatters, setSelectedBatters] = useState<string[]>([]);
	const [selectedBowlers, setSelectedBowlers] = useState<string[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	type Stat = "Runs" | "Average" | "Matches";
	const [selectedStat, setSelectedStat] = useState<Stat>("Runs");
	type BowlStat = "Wickets" | "Average" | "Economy";
	const [selectedBowlStat, setSelectedBowlStat] = useState<BowlStat>("Wickets");

	useEffect(() => {
		(async () => {
			const seasons = (await getSeasons(orgId)).slice(0, 10);

			const teamsList: Promise<Team[]>[] = [];
			seasons.forEach(async season => {
				const teams: Promise<Team[]> = getTeamGradeIds(orgId, season.Id);
				teamsList.push(teams);
			});

			const teams = await Promise.all(teamsList);

			const seasonTeams = seasons.map(async (season, idx) => ({
				seasonName: season.Name,
				teamsStats: await collectStats(teams[idx])
			}));

			const seasonTeamStats = await Promise.all(seasonTeams);

			const collectedSeasonStats = seasonTeamStats.map(season => ({
				name: season.seasonName,
				collectedBatStats: combineBatStats(season.teamsStats),
				collectedBowlStats: combineBowlStats(season.teamsStats),
				collectedFieldStats: combineFieldStats(season.teamsStats)
			}));

			const totalBatStats = collectedSeasonStats.map(season => {
				const playerRuns = season.collectedBatStats.map(stat => [
					stat.Name,
					selectedStat === "Runs"
						? stat.Statistics.BattingAggregate
						: selectedStat === "Matches"
							? stat.Statistics.Matches
							: Math.round(
									(stat.Statistics.BattingAggregate /
										(stat.Statistics.BattingInnings - stat.Statistics.BattingNotOuts)) *
										100
								) / 100
				]);
				const seasonObject: Record<string, string | number | null> = {
					name: season.name.split(" ")[1].replace("20", "")
				};
				playerRuns.forEach(([name, runs]) => {
					seasonObject[name] = runs;
				});
				return seasonObject;
			});

			const totalBowlStats = collectedSeasonStats.map(season => {
				const playerStats = season.collectedBowlStats.map(stat => [
					stat.Name,
					selectedBowlStat === "Wickets"
						? stat.Statistics.BowlingWickets
						: selectedBowlStat === "Average"
							? Math.round((stat.Statistics.BowlingRuns / stat.Statistics.BowlingWickets) * 100) /
									100 || null
							: Math.round((stat.Statistics.BowlingRuns / stat.Statistics.BowlingBalls) * 600) /
									100 || null
				]);
				const seasonObject: Record<string, string | number | null> = {
					name: season.name.split(" ")[1].replace("20", "")
				};
				playerStats.forEach(([name, stat]) => {
					seasonObject[name as string] = stat === Infinity ? null : stat;
				});
				return seasonObject;
			});

			const allBatters = uniq(
				totalBatStats
					.map(stats => Object.keys(stats).filter(player => player !== "name"))
					.reduce((acc, player) => [...acc, ...player], [])
			);

			setBatters(allBatters);
			setBatStats([{ "": null }, ...totalBatStats.reverse(), { "": null }]);
			setBowlStats([{ "": null }, ...totalBowlStats.reverse(), { "": null }]);
			setLoading(false);
		})();
	}, [selectedBowlStat, selectedStat, orgId]);

	useEffect(() => {
		if (batters.length) {
			const defaultPlayers = batters.slice(0, Math.min(4, batters.length));
			if (!selectedBatters.length) {
				setSelectedBatters(defaultPlayers);
			}
			if (!selectedBowlers.length) {
				setSelectedBowlers(defaultPlayers);
			}
		}
	}, [batters, selectedBatters.length, selectedBowlers.length]);

	if (loading) {
		return (
			<div className="relative min-h-[500px] rounded-2xl panel">
				<LoadingOverlay
					visible={true}
					overlayProps={{ radius: "lg", blur: 1 }}
					loaderProps={{
						children: (
							<motion.div
								initial={{ opacity: 0, scale: 0.8 }}
								animate={{ opacity: 1, scale: 1 }}
								className="flex flex-col justify-center items-center gap-6 p-8"
							>
								<div className="relative">
									<div className="w-16 h-16 pitch-gradient rounded-full flex items-center justify-center animate-pulse">
										<ChartBarIcon className="h-8 w-8 text-white" />
									</div>
									<div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full animate-bounce"></div>
								</div>
								<div className="text-center space-y-2">
									<p className="text-xl font-semibold text-slate-100">Compiling Statistics</p>
									<p className="text-slate-300">
										Analyzing cricket data and generating insights...
									</p>
								</div>
								<Loader color="green" size="sm" />
							</motion.div>
						)
					}}
				/>
			</div>
		);
	}

	return (
		<div className="p-6 space-y-8">
			<motion.section
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6 }}
				className="space-y-6"
			>
				<div className="flex items-center space-x-3 pb-4 border-b border-slate-700">
					<div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-cyan-400 rounded-xl flex items-center justify-center">
						<TrophyIcon className="h-6 w-6 text-white" />
					</div>
					<h2 className="text-4xl text-white">Batting Insights</h2>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div className="space-y-2">
						<label className="text-sm font-medium text-slate-300">Batting Metric</label>
						<Select
							variant="filled"
							data={["Runs", "Average", "Matches"]}
							value={selectedStat}
							onChange={stat => setSelectedStat(stat as Stat)}
							allowDeselect={false}
							classNames={{
								input: "bg-slate-900/70 border-slate-600 text-slate-100",
								label: "text-slate-300",
								dropdown: "bg-white border border-slate-300 text-slate-900",
								option:
									"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
							}}
						/>
					</div>
					<div className="md:col-span-2 space-y-2">
						<label className="text-sm font-medium text-slate-300">Select Players</label>
						<MultiSelect
							variant="filled"
							searchable
							data={batters}
							value={selectedBatters}
							onChange={setSelectedBatters}
							placeholder="Search and select batters..."
							classNames={{
								input: "bg-slate-900/70 border-slate-600 text-slate-100",
								label: "text-slate-300",
								dropdown: "bg-white border border-slate-300 text-slate-900",
								option:
									"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
							}}
						/>
					</div>
				</div>

				<div className="bg-slate-950/30 rounded-xl border border-slate-700 overflow-hidden">
					<InsightGraph stats={batStats} selectedBatters={selectedBatters} stat={selectedStat} />
				</div>
			</motion.section>

			<motion.section
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.6, delay: 0.2 }}
				className="space-y-6"
			>
				<div className="flex items-center space-x-3 pb-4 border-b border-slate-700">
					<div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
						<ChartBarIcon className="h-6 w-6 text-white" />
					</div>
					<h2 className="text-4xl text-white">Bowling Insights</h2>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div className="space-y-2">
						<label className="text-sm font-medium text-slate-300">Bowling Metric</label>
						<Select
							allowDeselect={false}
							variant="filled"
							data={["Wickets", "Average", "Economy"]}
							value={selectedBowlStat}
							onChange={stat => setSelectedBowlStat(stat as BowlStat)}
							classNames={{
								input: "bg-slate-900/70 border-slate-600 text-slate-100",
								label: "text-slate-300",
								dropdown: "bg-white border border-slate-300 text-slate-900",
								option:
									"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
							}}
						/>
					</div>
					<div className="md:col-span-2 space-y-2">
						<label className="text-sm font-medium text-slate-300">Select Players</label>
						<MultiSelect
							variant="filled"
							searchable
							data={batters}
							value={selectedBowlers}
							onChange={setSelectedBowlers}
							placeholder="Search and select bowlers..."
							classNames={{
								input: "bg-slate-900/70 border-slate-600 text-slate-100",
								label: "text-slate-300",
								dropdown: "bg-white border border-slate-300 text-slate-900",
								option:
									"text-slate-900 data-[hovered]:bg-slate-100 data-[checked]:bg-emerald-100 data-[checked]:text-slate-900"
							}}
						/>
					</div>
				</div>

				<div className="bg-slate-950/30 rounded-xl border border-slate-700 overflow-hidden">
					<InsightGraph
						stats={bowlStats}
						selectedBatters={selectedBowlers}
						stat={selectedBowlStat}
					/>
				</div>
			</motion.section>
		</div>
	);
};

export default Insights;
